import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { Home, Grid3X3, ShoppingCart, CalendarCheck, User, Package, Bell, LogIn } from 'lucide-react';

const CustomerLayout = ({ children, showHeader = true, showBottomNav = true, title }) => {
  const location = useLocation();
  const { user, storeSettings } = useAuth();
  const { totalItems } = useCart();
  const isSubscriptionsEnabled = storeSettings?.tenant_features?.subscriptions === true;
  const isGuest = !user;

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/products', icon: Grid3X3, label: 'Categories' },
    ...(isGuest ? [] : [{ path: '/orders', icon: Package, label: 'Orders' }]),
    ...(!isGuest && isSubscriptionsEnabled ? [{ path: '/subscriptions', icon: CalendarCheck, label: 'Subscribe' }] : []),
    ...(isGuest ? [{ path: '/login', icon: LogIn, label: 'Login' }] : [{ path: '/profile', icon: User, label: 'Profile' }]),
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[var(--theme-background)]">
      {/* Header */}
      {showHeader && (
        <header className="fixed top-0 left-0 right-0 z-40 h-16 flex items-center glass border-b border-black/5">
          <div className="w-full max-w-5xl mx-auto flex items-center justify-between px-4">
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-lg text-[var(--theme-text-main)] truncate">
                {title || storeSettings?.store_name || 'Fresh Delivery'}
              </h1>
            </div>
            
            {/* Cart + Notifications */}
            <div className="flex items-center gap-3 shrink-0">
              <Link
                to="/notifications"
                className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
                data-testid="header-notification-btn"
              >
                <Bell className="w-5 h-5 text-[var(--theme-text-main)]" />
              </Link>
              <Link
                to="/cart"
                className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
                data-testid="header-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 text-[var(--theme-text-main)]" />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[var(--theme-primary)] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {totalItems}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </header>
      )}

      {/* Main Content — constrained on larger screens */}
      {/* The header is `fixed`, so it doesn't reserve space in flow — content needs
          matching top padding. It must include the safe-area inset too: the CSS
          `.fixed.top-0` rule shifts the header itself down by that inset, so a
          plain `pt-16` alone would leave a gap under the header exactly as tall
          as the inset (or an overlap, depending on stacking) instead of clearing it. */}
      <main
        className={`max-w-5xl mx-auto ${showBottomNav ? 'pb-24' : ''}`}
        style={showHeader ? { paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px))' } : undefined}
      >
        {children}
      </main>

      {/* Bottom Navigation */}
      {showBottomNav && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--theme-surface)] border-t border-black/5 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="max-w-3xl mx-auto flex items-center justify-around py-2">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-colors ${active ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

export default CustomerLayout;
